import { html } from '@effectivastudio/lit-components';
import BaseMenu from '@effectivastudio/lit-components/components/base_menu/element.js';
import { classMap } from 'lit/directives/class-map.js';
import translations from './translations.js';
import style from './style.lit.css';
import '@effectivastudio/lit-components/components/dot_menu/element.js';
import '@effectivastudio/lit-components/components/dot_menu/element.js';
import '../icon/element.js';
/**
 * The dot-dot-dot menu for event card attend actions
 *
 * @prop cardId - The id of a tag
 * @prop cardTitle - Card title, used for ionic/native menu title
 * @prop attendingState - Attending status (options: initial, going, cannot)
 */

export default class CardAttendMenu extends BaseMenu {
  /** @internal */
  static translations = translations;
  static styles = [BaseMenu.styles, style];

  static properties = {
    cardId: { type: Number },
    cardTitle: { type: String },
    attendingState: { type: String, reflect: true },
  };

  /** @internal */
  status = {
    attend: 'going',
    canNotAttend: 'cannot',
    withdrawn: 'withdrawn',
  };

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();

    this.card =
      document.querySelector(`bu-card[cardId="${this.cardId}"]`) ||
      document.querySelector(`bu-card-event-line-1[cardId="${this.cardId}"]`);
    if (this.card) {
      this.card.attendMenu = this;
    }
  }

  /** @internal */
  get instanceId() {
    return this.cardId;
  }

  get currentUser() {
    return this.AppConfig?.user?.id;
  }

  /** @internal */
  get isIframeMode() {
    return document.documentElement.classList.contains('embedded');
  }

  /** @internal */
  get baseUrl() {
    return `/event_cards/${this.cardId}`;
  }

  /** @internal */
  get attendUrl() {
    return `${this.baseUrl}/attend`;
  }

  /** @internal */
  get canNotAttendUrl() {
    return `${this.baseUrl}/cannot`;
  }

  /** @internal */
  get withdrawUrl() {
    return `${this.baseUrl}/leave`;
  }

  /** @internal */
  get collection() {
    const collection = {};

    switch (this.attendingState) {
      case this.status.attend:
        collection.label = this.t('status.attend');
        collection.msg = this.t('snack.attend');
        collection.icon = 'circle-check:solid';
        break;
      case this.status.canNotAttend:
        collection.label = this.t('status.can_not_attend');
        collection.msg = this.t('snack.can_not_attend');
        collection.icon = 'circle-xmark:solid';
        break;
      default:
        collection.label = this.t('status.initial');
        collection.msg = this.t('snack.initial');
        collection.icon = null;
        break;
    }

    return collection;
  }

  /** @internal */
  get attendItem() {
    return {
      title: this.t('actions.attend'),
      action: this.actionFor('attend', [this.attendUrl]),
      icon: 'plus-circle',
      partName: 'attend',
    };
  }

  /** @internal */
  get canNotAttendItem() {
    return {
      title: this.t('actions.can_not_attend'),
      action: this.actionFor('attend', [this.canNotAttendUrl]),
      icon: 'minus-circle',
      partName: 'not-attend',
    };
  }

  /** @internal */
  get withdrawItem() {
    return {
      title: this.t('actions.withdraw'),
      action: this.actionFor('attend', [this.withdrawUrl]),
      icon: 'minus-circle',
      partName: 'withdraw',
    };
  }

  /** @internal */
  get attendOn() {
    return this.attendingState !== this.status.attend;
  }

  /** @internal */
  get canNotAttendOn() {
    return !this.attendingState;
  }

  /** @internal */
  get withdrawOn() {
    return this.attendingState === this.status.attend;
  }

  /** @internal */
  get classes() {
    return {
      'bu-btn': true,
      'btn-outline-secondary':
        !this.attendingState || this.attendingState === this.status.withdrawn,
      'btn-naked-dark':
        this.attendingState === this.status.attend ||
        this.attendingState === this.status.canNotAttend,
      attend: this.attendingState === this.status.attend,
      'not-attend': this.attendingState === this.status.canNotAttend,
    };
  }

  /** @internal */
  get items() {
    return [
      this.attendOn && this.attendItem,
      this.canNotAttendOn && this.canNotAttendItem,
      this.withdrawOn && this.withdrawItem,
    ].filter(Boolean);
  }

  /** @internal */
  get dotMenuConfig() {
    return {
      buttons: this.items,
      header: this.cardTitle,
      showCancel: true,
    };
  }

  actionFor(method, args) {
    return {
      menu: 'bu-card-attend-menu',
      id: this.instanceId,
      method: method,
      args: args,
    };
  }

  updateAvatarList() {
    const attendeesSrc = this.card.attendeesSrc;
    const user = {
      name: this.AppConfig?.user.fullName,
      picture: this.AppConfig?.user.avatar,
    };

    if (this.attendingState === 'withdrawn' || this.attendingState === null) {
      const objWithIdIndex = attendeesSrc.findIndex(
        obj => obj.name === user.name
      );

      attendeesSrc.splice(objWithIdIndex, 1);
    }

    if (this.attendingState === 'going') {
      attendeesSrc.push(user);
    }

    this.card.attendeesSrc = attendeesSrc;
  }

  async attend(url) {
    if (this.isIframeMode) {
      this.commonActions.visit(this.baseUrl, { target: '_blank' });
      return;
    }

    if (!this.currentUser) {
      this.commonActions.visit('/login');
      return;
    }

    try {
      const res = await this.commonActions.put(url);
      const resJSON = JSON.parse(await res.text());

      this.card.attendantsCountTaken = resJSON.attendantsCountTaken;
      this.attendingState = this.card.attendingState = resJSON.state;
      this.card.attendeesSrc &&
        this.updateAvatarList(); /* only for bu-card-event-line-1 */
      this.commonActions.displaySnackBarSuccess(this.collection.msg);
    } catch (res) {
      if (this.card.attendantsCountTaken >= this.card.attendeesLimit) {
        this.commonActions.displaySnackBarError(this.t('snack.slots_full'));
        return;
      }
      this.commonActions.displaySnackBarError(this.t('snack.generic_error'));
    }
  }

  renderIcon() {
    return html` <bu-icon .name=${this.collection.icon}></bu-icon> `;
  }

  render() {
    return html`
      <ef-dot-menu .config=${this.dotMenuConfig}>
        <button slot="trigger" class=${classMap(this.classes)}>
          ${this.collection.icon ? this.renderIcon() : null}
          <span>${this.collection.label}</span>
        </button>
      </ef-dot-menu>
    `;
  }
}

CardAttendMenu.registerTagName('bu-card-attend-menu');
CardAttendMenu.registerCommonActions('bu-card-attend-menu');

customElements.get('bu-card-attend-menu') ||
  customElements.define('bu-card-attend-menu', CardAttendMenu);
